import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { RefreshCw, AlertTriangle, TrendingDown, Layers, Info } from "lucide-react";

const MONO = { fontFamily: "'JetBrains Mono', monospace" };
const fmt = (v, d = 2) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(d));
const money = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  const a = Math.abs(n);
  return a >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;
};

const Th = ({ children, right, title }) => (
  <th className={`px-3 py-2 font-medium ${right ? "text-right" : "text-left"}`} title={title} style={{ whiteSpace: "nowrap" }}>{children}</th>
);

export const PmccView = () => {
  const [data, setData] = useState({ repair: [], screen: [], status: "never", screen_as_of: null });
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/screener/pmcc");
      setData(d);
      setBuilding(d.status === "running");
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!building) return;
    const t = setInterval(async () => {
      const { data: d } = await api.get("/screener/pmcc");
      setData(d);
      if (d.status !== "running") { setBuilding(false); clearInterval(t); }
    }, 4000);
    return () => clearInterval(t);
  }, [building]);

  const refresh = async () => {
    setBuilding(true);
    await api.post("/screener/pmcc/refresh");
    load();
  };

  const { repair, screen } = data;

  return (
    <div data-testid="pmcc-view">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>
          <b style={{ color: "var(--hoip-text-primary)" }}>PMCC / Fig Leaf</b> — replace capital-heavy stock with a deep-ITM LEAP and sell short calls against it.
        </p>
        <button onClick={refresh} disabled={building}
          className="h-8 px-3 text-xs rounded-md border transition-colors disabled:opacity-50 hover:bg-[var(--hoip-hover)] flex items-center gap-1.5"
          style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }} data-testid="pmcc-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${building ? "animate-spin" : ""}`} /> {building ? "Scanning…" : "Refresh pairs"}
        </button>
      </div>

      {/* ── Function A: Portfolio Repair ───────────────────────── */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <TrendingDown className="w-4 h-4" style={{ color: "#F59E0B" }} />
          <h3 className="text-sm font-semibold" style={{ color: "var(--hoip-text-primary)", fontFamily: "Chivo, sans-serif" }}>Portfolio Repair — PMCC Upgrade Alerts</h3>
        </div>
        {loading ? (
          <p className="text-xs px-1 py-6" style={{ color: "var(--hoip-text-tertiary)" }}>Scanning your positions…</p>
        ) : repair.length === 0 ? (
          <p className="text-xs px-1 py-6" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="pmcc-repair-empty">
            No held positions currently qualify (need held &gt;45 days, unrealized worse than −10%, and no open LEAP).
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {repair.map((c) => (
              <div key={c.ticker} className="rounded-lg border p-3.5" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid={`pmcc-repair-${c.ticker}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold" style={{ color: "var(--hoip-text-primary)" }}>{c.ticker}</span>
                  <span className="text-sm font-semibold" style={{ ...MONO, color: "#EF4444" }}>{fmt(c.unrealized_pct, 1)}%</span>
                </div>
                <div className="space-y-1 text-[11px]" style={{ color: "var(--hoip-text-secondary)" }}>
                  <div className="flex justify-between"><span>{c.shares} sh @ ${fmt(c.avg_cost, 2)} · now ${fmt(c.price, 2)}</span><span style={MONO}>{c.held_days}d held</span></div>
                  <div className="flex justify-between"><span>Tax-loss harvest</span><b style={{ ...MONO, color: "var(--hoip-text-primary)" }}>{money(c.tax_loss_value)}</b></div>
                  <div className="flex justify-between"><span>Collateral freed</span><b style={{ ...MONO, color: "#10B981" }}>{money(c.collateral_freed)}</b></div>
                  <div className="pt-1.5 mt-1.5 border-t" style={{ borderColor: "var(--hoip-border)" }}>
                    {c.leap ? (
                      <div className="flex items-start gap-1.5" style={{ color: "var(--hoip-text-primary)" }}>
                        <Layers className="w-3.5 h-3.5 mt-0.5" style={{ color: "#3B82F6" }} />
                        <span>Suggested LEAP: <b style={MONO}>{fmt(c.leap.delta, 2)}Δ ${fmt(c.leap.strike, 0)}</b> call, {c.leap.dte}d <span style={{ color: "var(--hoip-text-tertiary)" }}>(~{money(c.leap.cost)})</span></span>
                      </div>
                    ) : (
                      <span style={{ color: "var(--hoip-text-tertiary)" }}>No deep-ITM LEAP listed for this name.</span>
                    )}
                  </div>
                  <div className="flex items-start gap-1.5 pt-1.5 text-[10px]" style={{ color: "#F59E0B" }}>
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {c.warning}
                  </div>
                  {c.wash_sale_risk && (
                    <div className="flex items-start gap-1.5 text-[10px] font-medium" style={{ color: "#EF4444" }} data-testid={`pmcc-wash-${c.ticker}`}>
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> Wash-sale risk — position is &lt;31 days old; harvesting now may disallow the loss.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Function B: PMCC Screener ──────────────────────────── */}
      <div className="flex items-center gap-2 mb-2">
        <Layers className="w-4 h-4" style={{ color: "#3B82F6" }} />
        <h3 className="text-sm font-semibold" style={{ color: "var(--hoip-text-primary)", fontFamily: "Chivo, sans-serif" }}>PMCC Screener — New Opportunities</h3>
        {data.screen_as_of && <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>as of {new Date(data.screen_as_of).toLocaleString()}</span>}
      </div>
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: "var(--hoip-border)" }}>
        <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
              <Th title="Underlying symbol and current price">Ticker</Th>
              <Th title="Regime read for the underlying">Regime</Th>
              <Th right title="Deep-ITM long LEAP: delta · strike · DTE">LEAP (Δ/strike/DTE)</Th>
              <Th right title="LEAP debit paid (cost of the long call)">LEAP cost</Th>
              <Th right title="Short call sold against the LEAP: delta · strike · DTE">Short (Δ/strike/DTE)</Th>
              <Th right title="Premium collected from this cycle's short call">Short prem</Th>
              <Th right title="Net debit = LEAP cost − first short premium">Net debit</Th>
              <Th right title="Breakeven = LEAP strike + net debit per share">Break-even</Th>
              <Th right title="Monthly synthetic-dividend yield = short premium ÷ LEAP cost">Mo. yield</Th>
              <Th right title="Max profit if the stock finishes at/above the short strike at the short expiry">Max profit</Th>
              <Th right title="Probability the short call is NOT touched (stays below the short strike)">P(NT)</Th>
            </tr>
          </thead>
          <tbody>
            {building && screen.length === 0 ? (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-xs" style={{ color: "var(--hoip-text-tertiary)" }}><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Scanning the universe for liquid LEAP / short-call pairs…</td></tr>
            ) : screen.length === 0 ? (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-xs" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="pmcc-screen-empty">
                No liquid PMCC pairs found. Click "Refresh pairs" to scan. Illiquid LEAPs (wide spread, no open interest) are excluded by design.
              </td></tr>
            ) : screen.map((p) => (
              <tr key={p.ticker} className="border-b transition-colors hover:bg-[var(--hoip-hover)]" style={{ borderColor: "var(--hoip-border)" }} data-testid={`pmcc-pair-${p.ticker}`}>
                <td className="px-3 py-2">
                  <span className="font-medium" style={{ color: "var(--hoip-text-primary)" }}>{p.ticker}</span>
                  <span className="block text-[10px]" style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>${fmt(p.spot, 2)}</span>
                </td>
                <td className="px-3 py-2 text-[11px]" style={{ color: "var(--hoip-text-secondary)" }}>{p.regime}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "#3B82F6" }}>{fmt(p.leap_delta, 2)}Δ ${fmt(p.leap_strike, 0)} <span style={{ color: "var(--hoip-text-tertiary)" }}>{p.leap_dte}d</span></td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-primary)" }}>{money(p.leap_cost)}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-secondary)" }}>{fmt(p.short_delta, 2)}Δ ${fmt(p.short_strike, 0)} <span style={{ color: "var(--hoip-text-tertiary)" }}>{p.short_dte}d</span></td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "#10B981" }}>{money(p.short_premium)}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-primary)" }}>{money(p.net_debit)}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-secondary)" }}>${fmt(p.breakeven, 2)}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "#10B981" }}>{fmt(p.monthly_yield_pct, 1)}%</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-primary)" }}>{p.max_profit != null ? money(p.max_profit) : "—"}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-secondary)" }}>{fmt(p.p_no_touch, 0)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] mt-2 flex items-start gap-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>
        <Info className="w-3 h-3 mt-0.5 shrink-0" /> LEAP legs require Δ≥0.80 and DTE≥300 with liquidity (spread &lt;$0.50, &lt;0.5% of mid, or OI&gt;100). Short calls are 30–45 DTE, Δ0.25–0.35. Pairs shown only when lifetime short premium beats the net debit. For educational purposes only — not investment advice.
      </p>
    </div>
  );
};
